import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Typography, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useParams } from 'react-router-dom'
import { getChargebeeInstance } from './settings/Settings/BillingOptions/util/get-chargebee-instance'
import {
  CheckoutCompleteCard,
  CheckoutLogo,
  CheckoutPageTheme,
  SupportLine,
  preloadCheckoutLogo
} from './CheckoutComplete'

/**
 * How often an open checkout asks whether what it charges for has changed. The API answers from
 * Elasticsearch, not Auth0, so a short interval costs little (ENG-2276).
 */
const POLL_MS = 10_000

interface CheckoutSummary {
  status: 'ready' | 'paid' | 'replaced' | 'expired' | 'invalid'
  message?: string
  version?: string
  billingContactIsNew: boolean
}

type View =
  | { kind: 'loading' }
  | { kind: 'closed'; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'checkout'; billingContactIsNew: boolean; url: string; version: string }
  | { kind: 'updated' }
  | { kind: 'complete'; billingContactIsNew: boolean }

const SUMMARY_QUERY = `
  query hostedCheckout($token: String!) {
    hostedCheckout(token: $token) {
      status
      message
      version
      billingContactIsNew
    }
  }
`

const OPEN_MUTATION = `
  mutation openHostedCheckout($token: String!) {
    openHostedCheckout(token: $token) { url version }
  }
`

/** The page is public, so it calls the API without the app's Apollo client and its login token. */
async function request<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${process.env.PS_GRAPHQL_URL}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables })
  })
  // A gateway error returns HTML, not JSON. Its parse error must not reach the customer.
  const json = await response.json().catch((): null => null)
  if (json?.errors?.length) throw new Error(json.errors[0].message)
  if (!response.ok || !json?.data) throw new Error('The checkout could not be loaded.')
  return json.data
}

const fetchSummary = (token: string) =>
  request<{ hostedCheckout: CheckoutSummary }>(SUMMARY_QUERY, { token }).then((data) => data.hostedCheckout)

const openCheckout = (token: string) =>
  request<{ openHostedCheckout: { url: string; version: string } }>(OPEN_MUTATION, { token }).then(
    (data) => data.openHostedCheckout
  )

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    minHeight: '100vh',
    padding: theme.spacing(4, 2),
    boxSizing: 'border-box',
    backgroundColor: theme.palette.grey[100]
  },
  column: {
    width: '100%',
    maxWidth: 760,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    // CheckoutLogo is sized for a card; the page header shows it smaller.
    transform: 'scale(0.6)',
    margin: theme.spacing(-2, 0)
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'
  },
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    textAlign: 'center'
  },
  checkout: {
    width: '100%',
    // Chargebee sets the real height through the resize callback once the page loads.
    minHeight: '100vh'
  }
}))

/**
 * PaintScout's checkout page for an Admin checkout link (ENG-2276): `/checkout/<token>`.
 *
 * The API creates the Chargebee hosted page when the page opens, with seats counted from the
 * company's users at that moment, and Chargebee.js embeds it here. The page then polls the API.
 * When the link stops working (a newer link was sent, it expired, or the company paid) or what it
 * charges for changes (a user was added or removed), the embedded checkout is removed, so it
 * cannot be paid at the old price.
 */
export default function HostedCheckout() {
  return (
    <CheckoutPageTheme>
      <HostedCheckoutContent />
    </CheckoutPageTheme>
  )
}

function HostedCheckoutContent() {
  const classes = useStyles({})
  const { token } = useParams<{ token: string }>()
  const [view, setView] = useState<View>({ kind: 'loading' })
  // Read by the poll and the Chargebee callbacks, which outlive the render that made them.
  const viewRef = useRef<View>(view)
  viewRef.current = view

  const start = useCallback(async () => {
    setView({ kind: 'loading' })
    try {
      const summary = await fetchSummary(token)
      if (summary.status !== 'ready') {
        setView({ kind: 'closed', message: summary.message ?? 'This checkout link is not valid.' })
        return
      }
      const { url, version } = await openCheckout(token)
      setView({ kind: 'checkout', billingContactIsNew: summary.billingContactIsNew, url, version })
    } catch (error) {
      setView({ kind: 'error', message: (error as Error)?.message || 'The checkout could not be loaded.' })
    }
  }, [token])

  useEffect(() => {
    start()
  }, [start])

  // The success card shows the logo right after payment; have it cached by then.
  useEffect(() => {
    preloadCheckoutLogo()
  }, [])

  /** Removes the open checkout when the link stops working or its price changes. */
  const check = useCallback(async () => {
    const current = viewRef.current
    if (current.kind !== 'checkout') return
    // A hidden tab does not check; the focus event checks when the customer comes back.
    if (document.hidden) return
    let summary: CheckoutSummary
    try {
      summary = await fetchSummary(token)
    } catch {
      return // A failed check leaves the checkout alone; the next one tries again.
    }
    const latest = viewRef.current
    if (latest.kind !== 'checkout' || latest.url !== current.url) return
    if (summary.status === 'paid') {
      // The webhook can clear the link before Chargebee's success callback fires. The customer on
      // this page most likely just paid, so show the success card, not "already paid".
      setView({ kind: 'complete', billingContactIsNew: latest.billingContactIsNew })
    } else if (summary.status !== 'ready') {
      setView({ kind: 'closed', message: summary.message ?? 'This checkout link is not valid.' })
    } else if (summary.version !== latest.version) {
      setView({ kind: 'updated' })
    }
  }, [token])

  const checking = view.kind === 'checkout'
  useEffect(() => {
    if (!checking) return
    const interval = window.setInterval(check, POLL_MS)
    window.addEventListener('focus', check)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', check)
    }
  }, [checking, check])

  // Loading and the open checkout show no PaintScout chrome. Loading is blank because Chargebee's
  // page shows its own spinner right after; the logo and a card frame only the messages around it.
  if (view.kind === 'loading') return null

  if (view.kind === 'checkout') {
    return (
      <EmbeddedCheckout
        key={view.url}
        url={view.url}
        onStep={check}
        onSuccess={() => setView({ kind: 'complete', billingContactIsNew: view.billingContactIsNew })}
        onError={(message) => setView({ kind: 'error', message })}
      />
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.column}>
        {view.kind === 'complete' ? (
          <CheckoutCompleteCard outcome={view.billingContactIsNew ? 'new' : 'existing'} />
        ) : (
          <>
            <div className={classes.header}>
              <CheckoutLogo />
            </div>
            {(view.kind === 'closed' || view.kind === 'error') && (
              <div className={`${classes.card} ${classes.centered}`}>
                <Typography variant="body1">{view.message}</Typography>
                {view.kind === 'error' && (
                  <Button variant="contained" color="primary" onClick={start}>
                    Try again
                  </Button>
                )}
                <SupportLine />
              </div>
            )}
            {view.kind === 'updated' && (
              <div className={`${classes.card} ${classes.centered}`}>
                <Typography variant="h3">This checkout was updated</Typography>
                <Typography variant="body1">
                  Your account executive changed it after you opened it. Load the updated checkout to see the new total
                  before you pay.
                </Typography>
                <Button variant="contained" color="primary" onClick={start}>
                  Load updated checkout
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Chargebee's embedded checkout (createCheckout + mount). Chargebee.js builds the iframe inside the
 * container. Unmounting this component removes the container, and with it the iframe: that is how
 * the page takes away a checkout that is no longer valid.
 */
function EmbeddedCheckout(props: {
  url: string
  onStep: () => void
  onSuccess: () => void
  onError: (message: string) => void
}) {
  const classes = useStyles({})
  const containerRef = useRef<HTMLDivElement>(null)
  const containerId = useRef(`hosted-checkout-${Math.random().toString(36).slice(2)}`).current
  // The callbacks are read at call time, so a re-render never hands Chargebee stale ones.
  const propsRef = useRef(props)
  propsRef.current = props

  useEffect(() => {
    let cancelled = false
    const container = containerRef.current

    async function mount() {
      const instance = await getChargebeeInstance(false)
      if (cancelled) return
      // Chargebee's tutorial calls createCheckout on Chargebee.getInstance(). getChargebeeInstance
      // returns Chargebee.init()'s result, so use that when it has createCheckout and fall back to
      // getInstance() otherwise. A missing createCheckout throws, and the page shows its error.
      const cb = instance?.createCheckout ? instance : (window as any).Chargebee?.getInstance?.()
      const checkout = await cb.createCheckout({
        url: props.url,
        callbacks: {
          resize: (height: number) => {
            if (!cancelled && container) container.style.height = `${height}px`
          },
          step: () => propsRef.current.onStep(),
          success: () => propsRef.current.onSuccess(),
          error: (error: unknown) => {
            console.error('Embedded checkout failed', error)
            propsRef.current.onError('The checkout could not be loaded.')
          }
        }
      })
      if (cancelled) return
      checkout.mount(`#${containerId}`)
    }

    mount().catch((error) => {
      console.error('Embedded checkout failed', error)
      if (!cancelled) propsRef.current.onError('The checkout could not be loaded.')
    })

    return () => {
      cancelled = true
      // React removes the node too; clearing it first stops the iframe at once.
      if (container) container.innerHTML = ''
    }
  }, [props.url, containerId])

  return <div id={containerId} ref={containerRef} className={classes.checkout} />
}
